import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import {
  addGalleryImage,
  deleteGalleryImage,
  listGalleryImages,
  reorderGalleryImages,
  uploadGalleryImage,
} from "@/lib/gallery-api";
import { AccountShell } from "@/components/account/AccountShell";
import { GALLERY_ADMIN } from "@/constants/testIds";

export const GalleryAdminPage = () => {
  const { user, loading } = useAuth();
  const [images, setImages] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pendingUrl, setPendingUrl] = useState(null);
  const [caption, setCaption] = useState("");

  const refresh = useCallback(() => {
    listGalleryImages()
      .then(setImages)
      .catch(() => toast("Nepodařilo se načíst galerii"));
  }, []);

  useEffect(() => {
    if (user?.is_gm) refresh();
  }, [user, refresh]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const { image_url } = await uploadGalleryImage(file);
      setPendingUrl(image_url);
    } catch (err) {
      toast("Nahrání se nezdařilo", { description: err.response?.data?.detail ?? "Zkus to znovu." });
    } finally {
      setUploading(false);
    }
  };

  const handleAdd = async () => {
    if (!pendingUrl) {
      toast("Nejdřív nahraj obrázek");
      return;
    }
    try {
      await addGalleryImage({ image_url: pendingUrl, caption: caption.trim() || null });
      toast("Obrázek přidán do galerie");
      setPendingUrl(null);
      setCaption("");
      refresh();
    } catch (err) {
      toast("Přidání se nezdařilo", { description: err.response?.data?.detail ?? "Zkus to znovu." });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Opravdu odstranit tenhle obrázek z galerie?")) return;
    try {
      await deleteGalleryImage(id);
      refresh();
    } catch {
      toast("Smazání se nezdařilo");
    }
  };

  const handleMove = async (index, direction) => {
    if (!images) return;
    const target = index + direction;
    if (target < 0 || target >= images.length) return;
    const reordered = [...images];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setImages(reordered);
    try {
      await reorderGalleryImages(reordered.map((img) => img.id));
    } catch {
      toast("Změna pořadí se nezdařila");
      refresh();
    }
  };

  if (loading) {
    return (
      <section className="min-h-[70vh] px-6 pb-24 pt-40 text-center">
        <p className="font-display text-sm uppercase tracking-[0.2em] text-white/40">Načítám…</p>
      </section>
    );
  }

  if (!user) return <Navigate to="/ucet" replace />;

  if (!user.is_gm) {
    return (
      <AccountShell isGm={false} testId={GALLERY_ADMIN.guard}>
        <div className="mx-auto max-w-lg rounded-xl border border-white/10 bg-[#17171b] p-8 text-center">
          <p className="text-sm text-white/60">Správa galerie je jen pro GM.</p>
          <Link to="/ucet" className="mt-4 inline-block text-sm text-[#F0801F] hover:underline">
            Zpět na účet
          </Link>
        </div>
      </AccountShell>
    );
  }

  return (
    <AccountShell isGm={user.is_gm} testId={GALLERY_ADMIN.page}>
      <div className="mb-8">
        <p className="font-display text-xs font-600 uppercase tracking-[0.3em] text-[#F0801F]">
          O Projektu - správa
        </p>
        <h1 className="mt-3 font-display text-3xl font-600 uppercase tracking-tight text-white sm:text-4xl">
          Galerie
        </h1>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#17171b] p-6">
        <p className="mb-4 font-display text-xs font-600 uppercase tracking-[0.16em] text-white/50">
          Přidat nový obrázek
        </p>
        {pendingUrl && (
          <img src={pendingUrl} alt="" className="mb-3 h-40 w-full rounded-md border border-white/10 object-cover" />
        )}
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={handleFileChange}
          disabled={uploading}
          data-testid={GALLERY_ADMIN.uploadInput}
          className="w-full rounded-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white/70 outline-none file:mr-3 file:rounded file:border-0 file:bg-[#F0801F] file:px-3 file:py-1.5 file:text-xs file:font-600 file:uppercase file:text-[#141417]"
        />
        {uploading && <p className="mt-1.5 text-xs text-white/40">Nahrávám…</p>}
        <input
          type="text"
          placeholder="Popisek (volitelné)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          data-testid={GALLERY_ADMIN.captionInput}
          className="mt-3 w-full rounded-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white outline-none focus:border-[#F0801F]/60"
        />
        <button
          onClick={handleAdd}
          disabled={!pendingUrl}
          data-testid={GALLERY_ADMIN.addButton}
          className="mt-3 w-full rounded-md bg-[#F0801F] py-2.5 font-display text-xs font-600 uppercase tracking-[0.14em] text-[#141417] hover:bg-[#ff9438] disabled:cursor-not-allowed disabled:opacity-60"
        >
          Přidat do galerie
        </button>
      </div>

      <div className="mt-6 space-y-3">
        {images === null && <p className="text-sm text-white/40">Načítám…</p>}
        {images?.length === 0 && <p className="text-sm text-white/50">Galerie je zatím prázdná.</p>}
        {images?.map((img, i) => (
          <div
            key={img.id}
            data-testid={GALLERY_ADMIN.row}
            className="flex items-center gap-4 rounded-xl border border-white/10 bg-[#17171b] p-4"
          >
            <img src={img.image_url} alt="" className="h-16 w-16 shrink-0 rounded-md object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-white/80">{img.caption || "(bez popisku)"}</p>
            </div>
            <div className="flex shrink-0 gap-1.5">
              <button
                onClick={() => handleMove(i, -1)}
                disabled={i === 0}
                data-testid={GALLERY_ADMIN.moveUpButton}
                className="rounded-md border border-white/20 px-2.5 py-1.5 text-xs text-white/70 hover:border-[#F0801F]/60 hover:text-[#F0801F] disabled:opacity-30"
              >
                ↑
              </button>
              <button
                onClick={() => handleMove(i, 1)}
                disabled={i === images.length - 1}
                data-testid={GALLERY_ADMIN.moveDownButton}
                className="rounded-md border border-white/20 px-2.5 py-1.5 text-xs text-white/70 hover:border-[#F0801F]/60 hover:text-[#F0801F] disabled:opacity-30"
              >
                ↓
              </button>
              <button
                onClick={() => handleDelete(img.id)}
                data-testid={GALLERY_ADMIN.deleteButton}
                className="rounded-md border border-white/20 px-2.5 py-1.5 text-xs text-white/70 hover:border-red-400/60 hover:text-red-300"
              >
                Smazat
              </button>
            </div>
          </div>
        ))}
      </div>
    </AccountShell>
  );
};
